import type { RefObject } from 'react'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import _ from 'lodash'
import {
  Stack,
  Spacer,
  Box,
  VStack,
  FormControl as ChakraFormControl,
  FormErrorMessage as ChakraFormErrorMessage,
  Input as ChakraInput,
} from '@chakra-ui/react'
import DatePicker from 'react-datepicker'
import 'react-datepicker/dist/react-datepicker.css'

import type {
  DateFilterValue,
  InputFormProps as Props,
} from '../../../../interfaces'
import { getFieldsMapping } from '../../../../../utils/mediaLibrary'
import OperatorMenu from '../../OperatorMenu'

const CustomInput = (
  inputProps: React.HTMLProps<HTMLInputElement>,
  ref: React.Ref<HTMLInputElement>,
): React.ReactElement => {
  const { value, onClick, onChange } = inputProps
  return (
    <ChakraInput
      bgColor="white"
      value={value}
      onClick={onClick}
      onChange={onChange}
      placeholder="Pick a date"
      ref={ref}
    />
  )
}

const CustomMetadataDateInputForm = (props: Props): React.ReactElement => {
  const {
    searchQueryJson,
    customMetadataField,
    validationObject,
    groupId,
    filterId,
    initialFocusRef,
    setSearchQueryJson,
  } = props

  const [dirty, setDirty] = useState<boolean>(false)

  const expression = useMemo(
    () => searchQueryJson.filterGroups[groupId]?.expressions[filterId],
    [searchQueryJson, groupId, filterId],
  )

  const fullWidthForm = useMemo(
    () =>
      ['LAST_7_DAYS', 'LAST_30_DAYS', 'CUSTOM_RANGE'].includes(
        expression.operator,
      ),
    [searchQueryJson, groupId, filterId],
  )

  const error: string | null = useMemo(
    () => validationObject.errors[groupId][filterId],
    [validationObject, groupId, filterId],
  )

  const handleOperatorChange = useCallback(
    (newOperator: string) => {
      const newSearchQueryJson = _.cloneDeep(searchQueryJson)
      newSearchQueryJson.filterGroups[groupId].expressions[filterId].operator =
        newOperator
      newSearchQueryJson.filterGroups[groupId].expressions[filterId].value =
        getFieldsMapping({
          fieldName: expression.fieldName,
          customMetadataField,
        }).getInitialValue(newOperator)
      setSearchQueryJson(newSearchQueryJson)
    },
    [searchQueryJson, groupId, filterId],
  )

  const handleValueChange = useCallback(
    (newValue: DateFilterValue) => {
      const newSearchQueryJson = _.cloneDeep(searchQueryJson)
      newSearchQueryJson.filterGroups[groupId].expressions[filterId].value =
        newValue
      setSearchQueryJson(newSearchQueryJson)
    },
    [searchQueryJson, groupId, filterId],
  )

  useEffect(() => {
    (initialFocusRef as any).current?.input?.focus()
  }, [initialFocusRef, expression.operator])

  return (
    <ChakraFormControl width="full" isInvalid={error !== null && dirty}>
      <VStack width="full" spacing="0.5em">
        <Stack
          alignItems="stretch"
          width="full"
          flexDirection={fullWidthForm ? 'column' : 'row'}
          spacing={fullWidthForm ? '0.5em' : '0'}
        >
          <OperatorMenu
            width={fullWidthForm ? 'full' : '45%'}
            expression={expression}
            customMetadataField={customMetadataField}
            handleOperatorChange={handleOperatorChange}
          />

          {!fullWidthForm && <Spacer />}

          {['EXACTLY', 'EARLIER_THAN', 'LATER_THAN'].includes(
            expression.operator,
          ) && (
            <Box minWidth="45%" maxWidth="45%">
              <DatePicker
                ref={initialFocusRef as RefObject<any>}
                selected={
                  new Date((expression.value as { exact: string }).exact)
                }
                customInput={React.createElement(React.forwardRef(CustomInput))}
                onChange={(newDate: Date) => {
                  const newValue = {
                    relative: {},
                    exact: newDate.toISOString(),
                  }
                  handleValueChange(newValue)
                  setDirty(true)
                }}
              />
            </Box>
          )}

          {['CUSTOM_RANGE'].includes(expression.operator) && (
            <Box width="full">
              <DatePicker
                ref={initialFocusRef as RefObject<any>}
                selectsRange={true}
                startDate={
                  (
                    expression.value as {
                      to: string
                      from: string
                    }
                  ).from
                    ? new Date(
                        (
                          expression.value as {
                            to: string
                            from: string
                          }
                        ).from,
                      )
                    : null
                }
                endDate={
                  (
                    expression.value as {
                      to: string
                      from: string
                    }
                  ).to
                    ? new Date(
                        (
                          expression.value as {
                            to: string
                            from: string
                          }
                        ).to,
                      )
                    : null
                }
                onChange={update => {
                  if (Array.isArray(update)) {
                    handleValueChange({
                      from: update[0] ? update[0].toISOString() : '',
                      to: update[1] ? update[1].toISOString() : '',
                    })
                    setDirty(true)
                  }
                }}
                customInput={React.createElement(React.forwardRef(CustomInput))}
              />
            </Box>
          )}
        </Stack>

        <VStack width="full" alignItems={fullWidthForm ? 'start' : 'end'}>
          <ChakraFormErrorMessage maxWidth={fullWidthForm ? 'full' : '45%'}>
            {error}
          </ChakraFormErrorMessage>
        </VStack>
      </VStack>
    </ChakraFormControl>
  )
}

export default CustomMetadataDateInputForm
